import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { ProjectDtoKeysEnum } from '@zaoblako/shared/modules/project/enum/project-dto-keys.enum';
import { NotificationDtoKeysEnum } from '@zaoblako/shared/modules/notification/enums/notification-dto-keys.enum';
import { type UserFindInterface, UserFindDto } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import { ChecklistStatusEnum } from '@zaoblako/shared/modules/project/enum/checklist.status.enum';
import type { ReminderFindInterface } from '@zaoblako/shared/modules/notification/dtos/reminder/reminder-find.dto';
import { type PlanningUnitFindInterface, PlanningUnitFindDto } from '@zaoblako/shared/common/dtos/planning-unit/planning-unit-find.dto';
import { type MasterMilestoneFindInterface, MasterMilestoneFindDto } from '@zaoblako/shared/modules/catalog/dtos/master-milestone/master-milestone-find.dto';
import type { ChecklistTypeFindInterface } from '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';

import * as yup from 'yup';

export interface ChecklistFindInterface {
  /** Unique identifier. Cannot be changed. */
  id: number;
  /** Short checklist description */
  name: string;
  /** Short checklist description */
  description?: string;
  /** Checklist creation date and time */
  created: Date;
  /** Checklist last update date and time */
  updated: Date;
  /** Is checklist deleted */
  deleted: boolean;
  /** Current checklist status */
  status: ChecklistStatusEnum;
  /** Checklist item start date and time */
  startAt: Date;
  /** Checklist item end date and time */
  endAt: Date;
  /** Date and time when checklist item was finished */
  finishedAt?: Date;
  /** Checklist item finish comment */
  finishComment?: string;
  /** Is checklist done */
  checked: boolean;
  /** User responsible for checklist item execution */
  responsible: UserFindInterface[];
  /** Checklist creator */
  createdBy?: UserFindInterface;
  /** Checklist type */
  type: ChecklistTypeFindInterface;
  /** Related master milestone */
  masterMilestone?: MasterMilestoneFindInterface;
  /** Reminder for checklist participants */
  reminder?: ReminderFindInterface;
  /** Checklist repeat planning */
  planning?: PlanningUnitFindInterface;
}

/** Scheduled reminder notification */
export const ChecklistFindDto = SerdeDtoClass.create<ChecklistFindInterface>(ProjectDtoKeysEnum.CHECKLIST_FIND_DTO, {
  id: {
    label: 'Номер',
    serializer: yup.number(),
  },
  name: {
    label: 'Имя',
    serializer: yup.string(),
  },
  description: {
    optional: true,
    label: 'Описание',
    serializer: yup.string(),
  },
  created: {
    label: 'Создан',
    serializer: yup.date(),
  },
  updated: {
    label: 'Обновлено',
    serializer: yup.date(),
  },
  deleted: {
    label: 'Удалён',
    serializer: yup.boolean(),
  },
  status: {
    label: 'Тип',
    serializer: yup
      .string()
      .oneOf(Object.values(ChecklistStatusEnum)),
  },
  startAt: {
    label: 'Начало',
    serializer: yup.date(),
  },
  endAt: {
    label: 'Конец',
    serializer: yup.date(),
  },
  finishedAt: {
    label: 'Окончание',
    serializer: yup.date(),
  },
  finishComment: {
    optional: true,
    label: 'Комментарий при завершении',
    serializer: yup.string(),
  },
  checked: {
    label: 'Проверен',
    serializer: yup.boolean(),
  },
  responsible: {
    label: 'Пользователи',
    nested: UserFindDto,
    serializer: (_, nested) => yup.array(nested),
  },
  createdBy: {
    optional: true,
    label: 'Создатель',
    nested: UserFindDto,
    serializer: (_, nested) => nested,
  },
  type: {
    label: 'Тип',
    nested: ProjectDtoKeysEnum.CHECKLIST_TYPE_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  masterMilestone: {
    optional: true,
    label: 'Мастер-веха',
    nested: MasterMilestoneFindDto,
    serializer: (_, nested) => nested,
  },
  reminder: {
    optional: true,
    label: 'Напоминание',
    nested: NotificationDtoKeysEnum.REMINDER_FIND_DTO,
    serializer: (_, nested) => nested,
  },
  planning: {
    optional: true,
    label: 'Планирование повторений события',
    nested: PlanningUnitFindDto,
    serializer: (_, nested) => nested,
  },
});
